import type { Sprite } from 'pixi.js';
import { lerp } from '../../shared/scalar';
import { ballOf } from '../../sim/full/attacks/ball';
import { isSprinting } from '../../sim/full/skills/sprint';
import type { EnemyState, PropPhase, SimEvent, SimState } from '../../sim/state';
import { createBurster } from '../class-bursts';
import type { Family, RenderContext } from '../context';
import type { Frame } from '../frame';
import { type Shape, type Sheet, TAU, paintSheet, paintedLater } from '../paint';
import { REFERENCE_RADIUS } from '../textures-looks';
import { add, byId, hide, lookup, setTint } from '../util';
import { ViewPool } from '../views';
import { BALL_PIECES, DOT_RADIUS, UNIT } from './sportif-paint';

type BallShapes = Readonly<Record<keyof typeof BALL_PIECES, Shape>>;

const TRAIL = { dots: 5, calmDots: 2, gap: 4, radius: 5, alpha: 0.3 };
const SPIN = { normal: 0.6, calm: 0.15 };
const LIFT = { flight: 4, hop: 9 };
const GLOW = 16;
const BOOSTED_GLOW = 24;
const KNOCK_REACH = 96;
const STARS = { count: 3, orbit: 1.1, tilt: 0.35, above: 5, size: 0.22, turnsPerBeat: 0.7 };

interface BallView {
  readonly shadow: Sprite;
  readonly glow: Sprite;
  readonly trail: readonly Sprite[];
  readonly ball: Sprite;
}

interface Track {
  phase: PropPhase;
  since: number;
  fromX: number;
  fromY: number;
  target: number | undefined;
  seen: number;
}

export function createFootball(ctx: RenderContext): Family {
  const { layers, textures } = ctx;
  const sheet = paintedLater((): Sheet<keyof typeof BALL_PIECES> => paintSheet(BALL_PIECES));
  const burster = createBurster(ctx, 48);
  const tracks = new Map<number, Track>();
  const knocked = new Set<number>();
  let views: ViewPool<BallView> | undefined;
  let starViews: ViewPool<readonly Sprite[]> | undefined;
  let image = 0;

  function viewsOf(shapes: BallShapes): ViewPool<BallView> {
    views ??= new ViewPool<BallView>(
      () => ({
        shadow: add(layers.weapons, shapes.ballShadow),
        glow: add(layers.glow, textures.halo),
        trail: Array.from({ length: TRAIL.dots }, () => add(layers.glow, shapes.dot)),
        ball: add(layers.weapons, shapes.ball),
      }),
      (view) => {
        hide(view.shadow, view.glow, view.ball, ...view.trail);
      },
    );
    return views;
  }

  function starsOf(shapes: BallShapes): ViewPool<readonly Sprite[]> {
    starViews ??= new ViewPool<readonly Sprite[]>(
      () => Array.from({ length: STARS.count }, () => add(layers.fx, shapes.dot)),
      (sprites) => {
        hide(...sprites);
      },
    );
    return starViews;
  }

  function knockedBySprint(state: SimState, x: number, y: number): boolean {
    return state.players.some(
      (player) =>
        !player.downed &&
        isSprinting(player) &&
        (player.x - x) * (player.x - x) + (player.y - y) * (player.y - y) <=
          KNOCK_REACH * KNOCK_REACH,
    );
  }

  function liftOf(state: SimState, phase: PropPhase, track: Track, x: number, y: number): number {
    if (phase === 'flying') {
      return LIFT.flight;
    }
    if (phase !== 'bouncing' || track.target === undefined) {
      return 0;
    }
    const target = byId(state.enemies, track.target);
    if (target === undefined) {
      return 0;
    }
    const gone = Math.hypot(x - track.fromX, y - track.fromY);
    const left = Math.hypot(target.x - x, target.y - y);
    return LIFT.hop * Math.sin(Math.PI * (gone / Math.max(1, gone + left)));
  }

  function drawStars(enemy: EnemyState, sprites: readonly Sprite[], alpha: number, frame: Frame) {
    const { now, calm, palette, tempo } = frame;
    const x = lerp(enemy.prevX, enemy.x, alpha);
    const y = lerp(enemy.prevY, enemy.y, alpha);
    const turn = calm ? 0 : (now / tempo.ticksPerBeat) * STARS.turnsPerBeat * TAU;
    const size = Math.max(2, enemy.radius * STARS.size) / (DOT_RADIUS * UNIT);
    for (let index = 0; index < sprites.length; index += 1) {
      const star = sprites[index];
      if (star === undefined) {
        continue;
      }
      const angle = turn + (index * TAU) / STARS.count;
      star.visible = true;
      star.position.set(
        x + Math.cos(angle) * enemy.radius * STARS.orbit,
        y - enemy.radius - STARS.above + Math.sin(angle) * enemy.radius * STARS.tilt,
      );
      star.scale.set(size);
      setTint(star, palette.noyau);
    }
  }

  return {
    onEvent(event: SimEvent, state: SimState, frame: Frame): void {
      if (
        event.type === 'enemyStatusStarted' &&
        event.status === 'frozen' &&
        knockedBySprint(state, event.x, event.y)
      ) {
        knocked.add(event.id);
        const enemy = byId(state.enemies, event.id);
        burster.spawn(frame, {
          shape: textures.ring,
          now: frame.now,
          duration: 6,
          x: event.x,
          y: event.y,
          fromRadius: (enemy?.radius ?? 12) * 0.6,
          toRadius: (enemy?.radius ?? 12) * 1.8,
          tint: frame.palette.noyau,
          peak: 0.8,
        });
      } else if (event.type === 'playerFired') {
        const player = byId(state.players, event.playerId);
        const owned = player === undefined ? undefined : ballOf(state, player);
        if (player !== undefined && owned?.phase === 'flying') {
          burster.spawn(frame, {
            shape: textures.ring,
            now: frame.now,
            duration: 5,
            x: event.x + Math.cos(event.angle) * player.radius,
            y: event.y + Math.sin(event.angle) * player.radius,
            fromRadius: 4,
            toRadius: 18,
            tint: owned.boosted === true ? frame.palette.or : frame.palette.texte,
            peak: 0.6,
          });
        }
      }
    },
    reset(): void {
      tracks.clear();
      knocked.clear();
      starViews?.releaseAll();
      burster.bursts.clear();
    },
    update(state: SimState, alpha: number, frame: Frame): void {
      const props = state.props;
      if (props === undefined && views === undefined && starViews === undefined) {
        return;
      }
      const { shapes } = sheet.get();
      const pool = viewsOf(shapes);
      const { now, calm, palette, light, pulse } = frame;
      image += 1;
      pool.begin();
      for (const prop of props ?? []) {
        if (prop.kind !== 'ball') {
          continue;
        }
        let track = tracks.get(prop.id);
        if (track === undefined) {
          track = {
            phase: prop.phase,
            since: now,
            fromX: prop.x,
            fromY: prop.y,
            target: undefined,
            seen: image,
          };
          tracks.set(prop.id, track);
        }
        if (track.phase !== prop.phase || track.target !== prop.targetId) {
          track.phase = prop.phase;
          track.since = now;
          track.fromX = prop.prevX;
          track.fromY = prop.prevY;
          track.target = prop.targetId;
        }
        track.seen = image;
        const owner = byId(state.players, prop.playerId);
        if (prop.phase === 'held' || owner === undefined) {
          continue;
        }
        const view = pool.acquire(prop.id);
        const color = palette[lookup(ctx.classTokens, owner.classId, 'class')];
        const x = lerp(prop.prevX, prop.x, alpha);
        const y = lerp(prop.prevY, prop.y, alpha);
        const size = owner.radius / REFERENCE_RADIUS;
        const lift = liftOf(state, prop.phase, track, x, y);
        const rolling = prop.phase === 'returning';
        view.ball.visible = true;
        view.ball.position.set(x, y - lift);
        view.ball.rotation = (now - track.since) * (calm ? SPIN.calm : SPIN.normal) + prop.id;
        view.ball.scale.set(size);

        view.shadow.visible = true;
        view.shadow.position.set(x + lift * 0.35, y + lift * 0.2);
        view.shadow.scale.set(size * (1 - lift / 40));
        setTint(view.shadow, 0x000000);
        view.shadow.alpha = 0.25 * (1 - lift / 30);

        const boosted = prop.boosted === true;
        view.glow.visible = !rolling;
        if (!rolling) {
          view.glow.position.set(x, y - lift);
          view.glow.scale.set((boosted ? BOOSTED_GLOW : GLOW) / textures.halo.radius);
          view.glow.alpha = light.haloAlpha * (0.8 + 0.2 * pulse);
          setTint(view.glow, boosted ? palette.or : color);
        }

        const speed = Math.hypot(prop.vx, prop.vy);
        const dots = rolling || speed === 0 ? 0 : calm ? TRAIL.calmDots : TRAIL.dots;
        view.trail.forEach((dot, index) => {
          dot.visible = index < dots;
          if (!dot.visible) {
            return;
          }
          const back = (index + 1) * TRAIL.gap * UNIT * size;
          dot.position.set(x - (prop.vx / speed) * back, y - lift - (prop.vy / speed) * back);
          dot.scale.set(size * (TRAIL.radius / DOT_RADIUS) * (1 - (index + 1) / 15));
          dot.alpha = TRAIL.alpha * (1 - (index + 1) / (TRAIL.dots + 1));
          setTint(dot, color);
        });
      }
      pool.end();
      for (const [id, track] of tracks) {
        if (track.seen !== image) {
          tracks.delete(id);
        }
      }
      const starPool = starsOf(shapes);
      starPool.begin();
      for (const id of knocked) {
        const enemy = byId(state.enemies, id);
        if (enemy === undefined || enemy.hp <= 0 || enemy.statuses?.frozen === undefined) {
          knocked.delete(id);
        } else {
          drawStars(enemy, starPool.acquire(id), alpha, frame);
        }
      }
      starPool.end();
      burster.bursts.update(state, alpha, frame);
    },
    destroy(): void {
      burster.bursts.clear();
      knocked.clear();
      sheet.painted?.source.destroy();
    },
  };
}
